import { useRef, useState, useEffect, useCallback } from 'react';
import { passiveArg, getWindowSize } from './index';

export const useWindowSize = (defaultWidth = null, defaultHeight = null) => {
  const [win, setWin] = useState([defaultWidth, defaultHeight]);
  const handleResize = () => {
    window.requestAnimationFrame(() => {
      setWin(getWindowSize());
    });
  };
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize, passiveArg);
    return () => {
      window.removeEventListener('resize', handleResize, passiveArg);
    };
  }, []);
  return win;
};

export const useElementDimensions = () => {
  const elementRef = useRef(null);
  const [dimensions, setDimensions] = useState([0, 0]);

  const handleResize = useCallback(() => {
    if (elementRef.current) {
      setDimensions([
        elementRef.current.offsetWidth,
        elementRef.current.offsetHeight
      ]);
    }
  }, [elementRef]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize, passiveArg);
    return () => {
      window.removeEventListener('resize', handleResize, passiveArg);
    };
  }, [handleResize]);

  return [elementRef, dimensions];
};

export const useClickOutside = callback => {
  const elementRef = useRef(null);

  const clickHandler = useCallback(
    e => {
      if (elementRef.current === null) return;
      const target = e.target;
      if (!elementRef.current.contains(target)) callback();
    },
    [elementRef, callback]
  );

  useEffect(() => {
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  return elementRef;
};

export const useEscPress = callback => {
  const escHandler = useCallback(
    event => {
      if (event.key === 'Escape') callback();
    },
    [callback]
  );

  useEffect(() => {
    window.addEventListener('keydown', escHandler);

    return () => window.removeEventListener('keydown', escHandler);
  }, [escHandler]);
};

export function useIntersectionObserver(
  elementRef,
  { threshold = 0, root = null, rootMargin = '0%', freezeOnceVisible = false }
) {
  const [entry, setEntry] = useState();
  const frozen = entry?.isIntersecting && freezeOnceVisible;
  const updateEntry = ([entry]) => {
    setEntry(entry);
  };

  useEffect(() => {
    const node = elementRef?.current; // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return;

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);

  return entry;
}

export const useAppearFromBelow = () => {
  const ref = useRef(null);
  const entry = useIntersectionObserver(ref, {});
  const appeared =
    (!!entry?.isIntersecting && entry?.boundingClientRect.top > 0) ||
    entry?.boundingClientRect.top < 0;

  return [ref, appeared];
};
